import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { LabelId } from '@this+that/models';

import {
  useMoveToCategoryUserCommunication,
  useRemoveUserCommunication,
  useReportAsSpamUserCommunication,
} from './';
import { useUserCommunicationsStore } from '../../store';
import { navigateToInboxOrDoBox } from '../../utils/navigation';

interface UseMoveEmailReturn {
  handleMoveToCategory: (
    messageId: string | string[],
    category: string,
  ) => Promise<void>;
  handleMoveToSpam: (messageId: string | string[]) => Promise<void>;
  handleMoveToTrash: (messageId: string | string[]) => Promise<void>;
}

export function useMoveEmail(): UseMoveEmailReturn {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { handleMoveToCategoryUserCommunication } =
    useMoveToCategoryUserCommunication();
  const { handleReportAsSpamUserCommunication } =
    useReportAsSpamUserCommunication();
  const { handleRemoveUserCommunication } = useRemoveUserCommunication();

  const { primary, forums, updates, promotions, sent, social, draft } =
    useUserCommunicationsStore();

  const combined = [
    ...primary,
    ...forums,
    ...updates,
    ...promotions,
    ...sent,
    ...social,
    ...draft,
  ];

  const allValidMessages = Array.from(
    new Map(combined.map((msg) => [msg.messageId, msg])).values(),
  );

  const currentTab = searchParams.get('tab');

  const handleMoveToCategory = async (
    messageId: string | string[],
    category: string,
  ) => {
    const isSingleEmail = typeof messageId === 'string';
    const messageIds = Array.isArray(messageId) ? messageId : [messageId];

    const emails = allValidMessages.filter((uc) =>
      messageIds.includes(uc.messageId),
    );

    const hasSpamEmail = emails.some((email) =>
      email.labelIds?.includes(LabelId.SPAM),
    );

    if (isSingleEmail) {
      navigateToInboxOrDoBox(navigate, currentTab);
    }

    await handleMoveToCategoryUserCommunication(
      messageId,
      category,
      hasSpamEmail,
    );
  };

  const handleMoveToSpam = async (messageId: string | string[]) => {
    const isSingleEmail = typeof messageId === 'string';

    await handleReportAsSpamUserCommunication(messageId, {
      shouldNavigate: isSingleEmail,
    });
  };

  const handleMoveToTrash = async (messageId: string | string[]) => {
    const isSingleEmail = typeof messageId === 'string';

    await handleRemoveUserCommunication(messageId, {
      shouldNavigate: isSingleEmail,
    });
  };

  return {
    handleMoveToCategory,
    handleMoveToSpam,
    handleMoveToTrash,
  };
}
